import Downshift from "downshift";
import { Icon } from "Components/Icons";
import React from "react";
import { useMemo } from "react";
import { sizeClass, TypeaheadFLProps } from "Components/Inputs/TypeaheadFL";
import debounce from "lodash.debounce";
import { Spinner } from "Components/ReactstrapSlim";
import classNames from "classnames";
import { Manager, Reference, Popper } from "react-popper";
import { ModifierFn } from "popper.js";
import { KnownSearchAutocomplete } from "Models/Search";

const setWidth: ModifierFn = (data, options) => {
   (data.instance.popper as HTMLElement).style.width = `${data.instance.reference.clientWidth}px`;
   return data;
};

export type AutocompleteValue = string | undefined | null;

export interface WithUseAutocomplete<T> {
   isLoading: boolean
   options: T[],
   handleSearch: React.Dispatch<React.SetStateAction<AutocompleteValue>>
}

export interface BaseAutocompleteProps {
   placeholder?: string | undefined,
   icon?: Icon;
   size?: TypeaheadFLProps<KnownSearchAutocomplete>["size"];
   tabIndex?: number;
   defaultInputValue?: string | undefined;
   onKeyDown?: (e: React.KeyboardEvent) => void;
   className?: string,
   innerRef?: React.Ref<HTMLInputElement>
}

export interface AutocompleteProps<T> extends WithUseAutocomplete<T>, BaseAutocompleteProps {
   value: AutocompleteValue,
   onChange: (value: AutocompleteValue) => void,
   itemToString: (item: T | null) => string,
   isInvalid?: boolean,
   validationMessage?: string,
   filterOptions?: (item: T) => boolean,
   onItemSelected?: (item: T | null) => void,
}

export const Autocomplete = <T,>({ innerRef, value, onChange, itemToString, isInvalid, validationMessage, filterOptions, onItemSelected, placeholder, icon, size, tabIndex, defaultInputValue, onKeyDown, className, isLoading, options, handleSearch }: AutocompleteProps<T>) => {
   const debouncedHandleSearch = useMemo(() => { return debounce(handleSearch, 500); }, []);

   return (<Downshift<T>
      itemToString={item => itemToString(item).replace(/, USA/gi, "")}
      onChange={onItemSelected}
      inputValue={value === null ? "" : value}
      onInputValueChange={(inputValue) => { onChange(inputValue); debouncedHandleSearch(inputValue); }}
      stateReducer={(state, changes) => {
         switch (changes.type) {
            case Downshift.stateChangeTypes.blurInput:
            case Downshift.stateChangeTypes.mouseUp:
               return { ...changes, inputValue: state.inputValue }
            default:
               return changes;
         }
      }}
   >
      {({
         getInputProps,
         getItemProps,
         getLabelProps,
         getMenuProps,
         isOpen,
         inputValue,
         highlightedIndex,
         selectedItem,
         getRootProps,
      }) => {

         const rootProps = getRootProps(undefined, { suppressRefError: true });
         rootProps.className = classNames(
            rootProps.className,
            "rfl",
            {
               "rfl--invalid": isInvalid,
               "rfl--hasvalue": !!inputValue,
            },
            sizeClass("rfl-", size)
         );

         const inputProps = getInputProps({
            className: classNames("rfl-input form-control", { "rfl-input--hasicon": isLoading || icon != null }, sizeClass("form-control-", size)),
            ref: innerRef
         });

         const filteredOptions = filterOptions ? options.filter(option => filterOptions(option)) : options;

         const hasErrorMessage = !!validationMessage;

         return (
            <div className={classNames("dropdown", className)}>
               <Manager>
                  <Reference>
                     {referenceProps =>
                        <div {...rootProps} ref={referenceProps.ref}>
                           <input {...inputProps} onKeyDown={onKeyDown} />
                           {!hasErrorMessage && <label tabIndex={tabIndex} {...getLabelProps({ className: "rfl-label" })}>{placeholder}</label>}
                           {hasErrorMessage && <div className="rfl-valdation-message">
                              {validationMessage}
                           </div>}
                           <div className="rfl-icon rfl-icon--pntr-none">
                              {
                                 isLoading
                                    ? <Spinner size="sm" className="tc-icon-active" />
                                    : icon
                                       ? React.createElement(icon, { className: "tc-icon-active" })
                                       : null
                              }
                           </div>
                        </div>
                     }
                  </Reference>
                  { isOpen && <Popper
                     placement={"bottom-start"}
                     modifiers={{ setPopperWidth: { enabled: true, order: 849, fn: setWidth } }}>
                     {
                        popperProps =>
                           <ul {...getMenuProps({ ref: popperProps.ref, style: popperProps.style, className: classNames("dropdown-menu", { show: isOpen }) })} >
                              {filteredOptions.length
                                 ?
                                 filteredOptions.map((item, index) => (
                                    <li
                                       {...getItemProps({
                                          key: itemToString(item),
                                          index,
                                          item,
                                          className: classNames("dropdown-item text-truncate", { highlighted: index == highlightedIndex })
                                       })}
                                    >
                                       {itemToString(item).replace(/, USA/gi, "")}
                                    </li>
                                 ))
                                 : isLoading
                                    ? <li className="dropdown-item">Searching...</li>
                                    : <li className="dropdown-item">Nothing found</li>}
                           </ul>
                     }
                  </Popper> }
               </Manager>
            </div>
         );
      }}
   </Downshift>)
}